import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ApiFieldDataOption,
  ApiNestedProduct,
  ApiProductField,
  ApiProductPage,
} from '../../../../../core/models/product-api.models';
import { ApiInitData, ApiInitDataOption } from '../../../../../core/models/init-data.models';
import {
  ApiCustomerBank,
  formatBankDetails,
  mapCustomerBanks,
  parseBankDetails,
} from '../../../../../core/models/bank-details.models';
import {
  AssureRecord,
  getBottomSheetFields,
  validateAssuresData,
  validateSharesSum,
  validateSharesSumByInsuranceType,
} from '../../../../../core/utils/assure-validator.util';

@Component({
  selector: 'app-subscription-insured',
  templateUrl: './subscription-insured.component.html',
  styleUrls: ['./subscription-insured.component.scss'],
})
export class SubscriptionInsuredComponent implements OnChanges {
  @Input() page!: ApiProductPage;
  @Input() product!: ApiNestedProduct;
  @Input() initData: ApiInitData | null = null;
  @Input() assures: AssureRecord[] = [];
  @Output() assuresChange = new EventEmitter<AssureRecord[]>();

  sheetOpen = false;
  editingAssure: AssureRecord | null = null;
  sheetForm?: FormGroup;
  sheetError = '';
  assurePendingRemoval: AssureRecord | null = null;

  private parentesOptions: ApiFieldDataOption[] = [];
  private readonly typeInsuranceOptions: ApiFieldDataOption[] = [
    { CODE: 'vie', LABEL: 'Vie' },
    { CODE: 'décès', LABEL: 'Décès' },
  ];

  constructor(private readonly fb: FormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initData']) {
      this.parentesOptions = this.mapInitOptions(this.initData?.parentes);
    }
  }

  get bottomSheetFields(): ApiProductField[] {
    return getBottomSheetFields(this.page.fields ?? []);
  }

  get validationMessage(): string {
    const fields = this.bottomSheetFields;
    if (!this.assures.length) {
      return 'Ajoutez au moins un bénéficiaire.';
    }

    if (
      this.page.allBeneficiaryDataRequired &&
      !validateAssuresData(this.assures, fields, true)
    ) {
      return 'Remplissez toutes les informations des bénéficiaires.';
    }

    if (this.page.canCheckPartAmount && this.page.allBeneficiaryDataRequired) {
      if (this.product.canCheckByInsuranceType) {
        if (!validateSharesSumByInsuranceType(this.assures, fields)) {
          return 'La somme des parts vie et décès doit être égale à 100 %.';
        }
      } else if (!validateSharesSum(this.assures, fields)) {
        return 'La somme des parts doit être égale à 100 %.';
      }
    }

    return '';
  }

  get canAddInsured(): boolean {
    return this.product['hasInsuredAddButton'] !== false;
  }

  get bankOptions(): ApiCustomerBank[] {
    return mapCustomerBanks(this.initData?.banks ?? this.initData?.banque_client);
  }

  visibleCardFields(assure: AssureRecord): ApiProductField[] {
    return this.bottomSheetFields.filter((field) => {
      if (this.fieldType(field) !== 'toggle') {
        return true;
      }
      return parseBankDetails(assure[field.name]).enabled;
    });
  }

  cardFieldLabel(field: ApiProductField): string {
    if (this.fieldType(field) === 'toggle') {
      return 'Prélèvement bancaire';
    }
    return field.label || field.name;
  }

  canRemoveAssure(assure: AssureRecord): boolean {
    if (this.page.canRemoveBeneficiary === false) {
      return false;
    }
    if (this.product.canCheckByInsuranceType) {
      return this.insuranceTypeOf(assure) !== 'vie';
    }
    return true;
  }

  openAddSheet(): void {
    this.editingAssure = null;
    this.sheetError = '';
    this.sheetForm = this.buildSheetForm({});
    this.sheetOpen = true;
  }

  openEditSheet(assure: AssureRecord): void {
    this.editingAssure = assure;
    this.sheetError = '';
    this.sheetForm = this.buildSheetForm(assure);
    this.sheetOpen = true;
  }

  closeSheet(): void {
    this.sheetOpen = false;
    this.editingAssure = null;
    this.sheetForm = undefined;
    this.sheetError = '';
  }

  onSheetBackdropMouseDown(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeSheet();
    }
  }

  isSelectField(field: ApiProductField): boolean {
    const type = this.fieldType(field);
    return (
      type === 'select' ||
      type === 'typeinsurance' ||
      type === 'parente' ||
      (field.dataListType || '').toLowerCase() === 'parente' ||
      field['isParente'] === true
    );
  }

  optionValue(option: ApiFieldDataOption): string {
    const record = option as Record<string, unknown>;
    const code = String(record['CODE'] ?? record['code'] ?? '').trim();
    if (code) {
      return code;
    }
    return String(record['LABEL'] ?? record['label'] ?? '').trim();
  }

  optionLabel(option: ApiFieldDataOption): string {
    const record = option as Record<string, unknown>;
    const label = String(record['LABEL'] ?? record['label'] ?? '').trim();
    return label || this.optionValue(option);
  }

  saveSheet(): void {
    if (!this.sheetForm) {
      return;
    }

    this.sheetForm.markAllAsTouched();
    if (this.sheetForm.invalid) {
      return;
    }

    const payload = this.normalizeSheetValues(this.sheetForm.getRawValue());
    const id = this.editingAssure?.['id'] ?? Date.now();
    const nextAssure: AssureRecord = {
      ...(this.editingAssure ?? {}),
      ...payload,
      id,
    };
    const typeField = this.bottomSheetFields.find((field) => this.fieldType(field) === 'typeinsurance');
    const typeRaw =
      payload['typeInsurance'] ??
      (typeField?.name ? payload[typeField.name] : undefined) ??
      nextAssure['typeInsurance'];
    if (this.product.canCheckByInsuranceType || typeField) {
      nextAssure['typeInsurance'] = typeRaw != null && typeRaw !== '' ? String(typeRaw) : 'vie';
    }

    const nextList = [...this.assures];
    const index = nextList.findIndex((item) => item['id'] === id);
    if (index >= 0) {
      nextList[index] = nextAssure;
    } else {
      nextList.push(nextAssure);
    }

    this.assuresChange.emit(nextList);
    this.closeSheet();
  }

  removeAssure(assure: AssureRecord): void {
    if (this.page.canRemoveBeneficiary === false || !this.canRemoveAssure(assure)) {
      return;
    }

    this.assurePendingRemoval = assure;
  }

  cancelRemoveAssure(): void {
    this.assurePendingRemoval = null;
  }

  confirmRemoveAssure(): void {
    const assure = this.assurePendingRemoval;
    this.assurePendingRemoval = null;
    if (!assure) {
      return;
    }

    this.assuresChange.emit(this.assures.filter((item) => item['id'] !== assure['id']));
  }

  displayValue(field: ApiProductField, assure: AssureRecord): string {
    const raw = assure[field.name];
    if (this.fieldType(field) === 'toggle') {
      return formatBankDetails(raw);
    }
    if (raw == null || raw === '') {
      return '—';
    }

    if (this.isParenteField(field)) {
      const match = this.findParenteOption(String(raw));
      return match ? this.optionLabel(match) : String(raw);
    }

    if (this.fieldType(field) === 'typeinsurance') {
      const value = String(raw).toLowerCase();
      if (value === 'vie') {
        return 'Vie';
      }
      if (value === 'décès' || value === 'deces') {
        return 'Décès';
      }
    }

    return String(raw);
  }

  fieldType(field: ApiProductField): string {
    return (field.type || 'text').toLowerCase();
  }

  selectOptions(field: ApiProductField): ApiFieldDataOption[] {
    if (this.fieldType(field) === 'typeinsurance') {
      return this.typeInsuranceOptions;
    }
    if (this.isParenteField(field)) {
      return this.parentesOptions;
    }
    return field.dataList ?? [];
  }

  trackSelectOption = (_index: number, option: ApiFieldDataOption): string =>
    this.optionValue(option) || this.optionLabel(option);

  compareSelectValues = (left: unknown, right: unknown): boolean =>
    String(left ?? '') === String(right ?? '');

  sheetControl(name: string): FormControl {
    return this.sheetForm?.get(name) as FormControl;
  }

  private buildSheetForm(values: AssureRecord): FormGroup {
    const group: Record<string, FormControl> = {};
    for (const field of this.bottomSheetFields) {
      const validators =
        this.fieldType(field) === 'toggle'
          ? []
          : field.holdData || field.obligatoire
            ? [Validators.required]
            : [];
      let value = values[field.name] ?? field.valeurParDefaut ?? '';
      if (this.fieldType(field) === 'toggle') {
        value = parseBankDetails(values[field.name]);
      } else if (this.isParenteField(field)) {
        value = this.optionValue(this.findParenteOption(String(value ?? '')) ?? { CODE: String(value ?? '') });
      }

      if (this.fieldType(field) === 'date' && typeof value === 'string' && value.includes('/')) {
        const parts = value.split('/');
        if (parts.length === 3) {
          value = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      group[field.name] = this.fb.control(value, validators);
    }
    return this.fb.group(group);
  }

  private normalizeSheetValues(raw: Record<string, unknown>): AssureRecord {
    const out: AssureRecord = {};
    for (const field of this.bottomSheetFields) {
      let value = raw[field.name];
      if (this.fieldType(field) === 'date' && typeof value === 'string' && value.includes('-')) {
        const [year, month, day] = value.split('-');
        if (year && month && day) {
          value = `${day}/${month}/${year}`;
        }
      }
      out[field.name] = this.isParenteField(field) ? this.normalizeParenteValue(value) : value;
    }
    return out;
  }

  private isParenteField(field: ApiProductField): boolean {
    return (
      this.fieldType(field) === 'parente' ||
      (field.dataListType || '').toLowerCase() === 'parente' ||
      field['isParente'] === true
    );
  }

  private findParenteOption(raw: string): ApiFieldDataOption | undefined {
    const needle = raw.trim().toLowerCase();
    if (!needle) {
      return undefined;
    }
    return this.parentesOptions.find((option) => {
      const code = this.optionValue(option).toLowerCase();
      const label = this.optionLabel(option).toLowerCase();
      return code === needle || label === needle;
    });
  }

  private normalizeParenteValue(value: unknown): string {
    const match = this.findParenteOption(String(value ?? ''));
    return match ? this.optionValue(match) : String(value ?? '').trim();
  }

  private insuranceTypeOf(assure: AssureRecord): string {
    const typeField = this.bottomSheetFields.find((field) => this.fieldType(field) === 'typeinsurance');
    const raw = (typeField?.name && assure[typeField.name]) || assure['typeInsurance'];
    return String(raw ?? '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private mapInitOptions(options: ApiInitDataOption[] | undefined): ApiFieldDataOption[] {
    return (options ?? [])
      .map((option) => {
        const record = option as Record<string, unknown>;
        const code = String(record['CODE'] ?? record['code'] ?? '').trim();
        const label = String(record['LABEL'] ?? record['label'] ?? '').trim();
        return { CODE: code, LABEL: label || code };
      })
      .filter((option) => option.CODE || option.LABEL);
  }
}
